import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { message } from 'antd';
import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { taskCompletedProperties } from '../../analytics/analyticsHelpers';
import { WorkspaceAccessResponse, getUsersWithAccessToSpace } from '../../api/workspace';
import { useGetUserFromAccessToken } from '../../common/auth';
import colorTokens from '../../constants/design_system/colors';
import { useViewportHeight } from '../../hooks/useIsMobile';
import { useSprintHook } from '../../hooks/useSprintHook';
import { useTaskFilterManagement } from '../../hooks/useTaskFilterManagement';
import { queryClient } from '../../queryClient';
import { optimisticUpdateListItem } from '../../serverState/helpers/optimisticCache';
import { updateSpaceTasksOrder } from '../../serverState/helpers/tasksOrderHelper';
import { queryKeys } from '../../serverState/queryKeys';
import { useGetActiveSprintByWorkspace } from '../../serverState/sprint';
import { useCreateTaskInWorkspace, useGetTasks, useUpdateTaskMutation } from '../../serverState/tasks';
import { useGetWorkspace } from '../../serverState/workspace';
import { useFilterStore } from '../../stores/filterStore';
import { Column } from '../../types/SprintBoardtypes';
import Task from '../../types/Task';
import { TaskStatus, TaskStatusGroup, defaultTaskStatuses } from '../../types/TaskStatus';
import Workspace, { WorkspaceFramework } from '../../types/Workspace';
import { WorkspaceView, WorkspaceViewGroup } from '../../types/WorkspaceView';
import { EndSprintModal } from '../Modal/EndSprintModal';
import TaskFiltersModal from '../Modal/TaskFiltersModal';
import { applySearchAndFilters, clearOwnerFilter, updateOwnerFilter } from '../TaskManagement/helpers/filterHelpers';
import { FiltersHeader } from '../TaskManagement/UI/FiltersHeader';
import { SprintHeader } from '../TaskManagement/UI/SprintHeader';
import { TaskTableDataType } from '../TaskTable/types/TaskTableTypes';
import { NoSprintInProgress } from '../UI/NoSprintInProgress';
import { TasksZeroState } from '../UI/TasksZeroState';
import ColumnContainer from './ColumnContainer';
import TaskCard from './TaskCard';

type WorkspaceContextType = {
  workspace?: Workspace;
  dataSource: Task[];
  setDataSource: React.Dispatch<React.SetStateAction<TaskTableDataType[]>>;
  allTasks: Task[];
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  setUsersWithAccessToSpace: React.Dispatch<React.SetStateAction<WorkspaceAccessResponse[]>>;
};

export const WorkSpaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export type BoardProps = {
  activeView: WorkspaceView;
  framework: WorkspaceFramework;
  onImportClick?: () => void;
};

const Board = (props: BoardProps) => {
  const workspaceQuery = useGetWorkspace();
  const spaceId = workspaceQuery.data?.id;
  const activeSprintQuery = useGetActiveSprintByWorkspace(workspaceQuery.data);
  const tasksQuery = useGetTasks(Number(spaceId));
  const updateTaskMutation = useUpdateTaskMutation();
  const currentUserQuery = useGetUserFromAccessToken();
  const workspaceFramework = workspaceQuery.data?.framework;

  let taskStatuses: TaskStatus[] = defaultTaskStatuses;
  const isKanbanOrSprint =
    props.activeView.group === WorkspaceViewGroup.kanban || props.activeView.group === WorkspaceViewGroup.sprint;

  const [usersWithAccessToSpace, setUsersWithAccessToSpace] = useState<WorkspaceAccessResponse[]>([]);
  const [dataSource, setDataSource] = useState<TaskTableDataType[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  const [endSprintModalVisible, toggleEndSprintModal] = useState(false);

  const [columns, setColumns] = useState<Column[]>([]);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);

  const [activeColumn, setActiveColumn] = useState<Column | null>(null);

  const [activeTask, setActiveTask] = useState<TaskTableDataType | null>(null);
  const [draggingHapenned, setDraggingHapenned] = useState(false);

  const viewportHeight = useViewportHeight();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    taskStatuses = ((workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses).sort((a, b) => {
      const orderA = Object.values(TaskStatusGroup).indexOf(a.group);
      const orderB = Object.values(TaskStatusGroup).indexOf(b.group);
      if (orderA === orderB) {
        return (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id);
      }
      return orderA - orderB;
    });
    setColumns(
      taskStatuses.map((status) => {
        return { id: status.title, status: status };
      })
    );
  }, [workspaceQuery.data]);

  useEffect(() => {
    if (tasksQuery.data) {
      const newTasksForTable = tasksQuery.data
        // Filter out duplicate tasks by id and if sprint is active filter out tasks that are not in the sprint
        .filter(
          (task, index, self) =>
            index === self.findIndex((t) => t.id === task.id) &&
            (isKanbanOrSprint ? task.sprintId === activeSprintQuery.data?.sprint?.id : true)
        )
        .map((t) => {
          const rt: Partial<TaskTableDataType> = t;
          rt.key = t.id;

          return rt;
        });

      setDataSource([...(newTasksForTable as TaskTableDataType[])]);
    }
  }, [tasksQuery.data, activeSprintQuery.data, props.activeView.group]);

  const fetchAccessToSpace = (spaceId: number) => {
    getUsersWithAccessToSpace(spaceId)
      .then((r) => {
        if (r.data?.length > 0) {
          setUsersWithAccessToSpace([...r.data]);
        } else {
          setUsersWithAccessToSpace([]);
        }
      })
      .catch((e) => console.log(e));
  };

  useEffect(() => {
    fetchAccessToSpace(Number(spaceId));
  }, [spaceId]);

  const handleCreateTaskInSprint = useCallback(
    async (statusId: number, addToTop: boolean) => {
      await createTask(statusId, addToTop);
    },
    [activeSprintQuery]
  );

  const { createTaskInKanban } = useCreateTaskInWorkspace();

  const createTask = async (statusId: number, addToTop: boolean) => {
    try {
      const savedTask = (await createTaskInKanban(
        statusId,
        isKanbanOrSprint ? activeSprintQuery.data?.sprint?.id : undefined,
        addToTop
      )) as TaskTableDataType;

      setDataSource([...dataSource.filter((t) => t.id !== savedTask.id), savedTask]);
      setTaskAddedOrChanged(true);

      setTimeout(() => {
        const taskTitleInput = document.querySelector(`[task-title-input-id='${savedTask.id}']`);
        if (taskTitleInput) {
          taskTitleInput.focus();
          taskTitleInput.select();
        }
      }, 100);
    } catch (error: any) {
      void messageApi.error(`Failed to create task: ${error.message}`);
    }
  };

  const [tasksOrders, settasksOrders] = useState({} as Record<string, TaskTableDataType[]>);
  const [taskAddedOrChanged, setTaskAddedOrChanged] = useState(false);

  const currWorkspaceQuery = useGetWorkspace();
  const statusOptions: TaskStatus[] = (currWorkspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses;

  const {
    filters,
    setFilters,
    selectedFilters,
    isViewSelectedFilters,
    ownerFilter,
    handleSaveForAll,
    handleResetToSaved,
  } = useTaskFilterManagement(props.activeView, usersWithAccessToSpace, () => {
    messageApi.success('Space filters saved');
    // Confirming "save for everyone" should dismiss the filters panel. #2645
    setIsFiltersModalOpen(false);
  });

  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);

  // Search text lives in the filter store keyed by (workspace, view) so it survives
  // navigating into a task view and back — same as the table view. Each view keeps its
  // own search, so no imperative clear is needed when switching views. #2276
  const searchValue = useFilterStore((s) => s.getSearch(props.activeView.workspaceId, props.activeView.id));
  const setSearchInStore = useFilterStore((s) => s.setSearch);
  const setSearchValue = useCallback(
    (value: string) => setSearchInStore(props.activeView.workspaceId, props.activeView.id, value),
    [setSearchInStore, props.activeView.workspaceId, props.activeView.id]
  );

  const { unfinishedTasks, handleEndSprint } = useSprintHook(
    (message: string) => messageApi.success(message),
    (message: string) => messageApi.error(message)
  );

  useEffect(() => {
    const filteredData = applySearchAndFilters(
      selectedFilters,
      dataSource,
      searchValue,
      currentUserQuery.data?.user.id,
      workspaceQuery.data?.estimationType,
      workspaceQuery.data?.activeSprintId
    );
    if (activeTask && !filteredData.find((item) => item.id === activeTask.id)) {
      filteredData.push(activeTask);
    }

    const orders: Record<string, TaskTableDataType[]> = {} as Record<string, TaskTableDataType[]>;
    const sortBy = workspaceQuery.data?.TaskOrder[0]?.taskIds || [];
    const sortedData = filteredData.sort((a, b) => {
      const aIndex = sortBy.indexOf(a.id);
      const bIndex = sortBy.indexOf(b.id);
      if (aIndex === -1 && bIndex === -1) {
        return a.id - b.id;
      }
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
    statusOptions.forEach((status) => {
      orders[status.id] = sortedData.filter((task) => task.statusId === status.id);
    });
    settasksOrders(orders);
    // TaskOrder[0].taskIds is read inside this effect and drives the sort; without it in
    // deps, a reorder from another user (broadcast as spaceUpdated) won't re-sort the board.
  }, [activeSprintQuery.data, dataSource, filters, searchValue, workspaceQuery.data?.TaskOrder]);

  // Scroll to top when search value changes
  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollTop = 0;
    }
  }, [searchValue]);

  // Scroll to top when the applied filters change (DEV-2524)
  const filtersSignature = JSON.stringify(selectedFilters);
  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollTop = 0;
    }
  }, [filtersSignature]);

  // The two effects above only reset the outer board scroll container (divRef). Each column's
  // own vertical scroll (`parentRef` in ColumnContainer.tsx) is a separate DOM node that was
  // never reset, which is why filtering/searching still leaves individual columns scrolled down.
  // Pass a combined signal down so each column resets its own scroll position too. #2524
  const filterScrollKey = `${searchValue}|${filtersSignature}`;

  // Handle scroll position with better timing
  useLayoutEffect(() => {
    if (divRef.current && !taskAddedOrChanged && !draggingHapenned) {
      divRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [tasksOrders, taskAddedOrChanged, draggingHapenned]);

  useEffect(() => {
    setTimeout(() => {
      setTaskAddedOrChanged(true);
    }, 1000);
  }, []);

  const divRef = useRef<HTMLDivElement>(null);

  // Memoize context to prevent all children re-rendering on every Board update
  const workspaceContextValue = useMemo(
    () => ({
      workspace: workspaceQuery.data,
      dataSource,
      setDataSource,
      allTasks: tasksQuery.data ?? [],
      usersWithAccessToSpace,
      setUsersWithAccessToSpace,
    }),
    [workspaceQuery.data, dataSource, tasksQuery.data, usersWithAccessToSpace]
  );

  if (
    !activeSprintQuery.data &&
    workspaceFramework === WorkspaceFramework.scrum &&
    props.activeView.group === WorkspaceViewGroup.sprint
  ) {
    return <NoSprintInProgress />;
  }

  // Check if we should show zero state (no tasks)
  const showZeroState = !tasksQuery.isLoading && (tasksQuery.data ?? []).length === 0;
  const showTasks = !tasksQuery.isLoading && (tasksQuery.data ?? []).length > 0;

  const isSprint =
    workspaceFramework === WorkspaceFramework.scrum && props.activeView.group === WorkspaceViewGroup.sprint;

  return (
    <div>
      {contextHolder}
      {isFiltersModalOpen && (
        <TaskFiltersModal
          isOpen={isFiltersModalOpen}
          onClose={() => setIsFiltersModalOpen(false)}
          onSave={(selectedFilters) => setFilters(selectedFilters)}
          filters={filters}
          viewSelectedFilters={props.activeView.filters || []}
          isViewSelectedFilters={isViewSelectedFilters}
          resetToSavedFilters={handleResetToSaved}
          saveFiltersForAll={() => handleSaveForAll(filters)}
        />
      )}
      <div className="header-bar flex justify-between ">
        <FiltersHeader
          activeView={props.activeView}
          ownerFilter={ownerFilter}
          onSaveOwnerFilter={(ownerFilter) => setFilters(updateOwnerFilter(filters, ownerFilter) || filters)}
          onClearOwnerFilter={() => setFilters(clearOwnerFilter(filters))}
          isViewSelectedFilters={isViewSelectedFilters}
          selectedFiltersLength={selectedFilters.length}
          onFiltersClick={() => setIsFiltersModalOpen(!isFiltersModalOpen)}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          createTask={() => createTask(columns[0].status.id, true)}
          showMessage={(message) => messageApi.success(message)}
          resetToSavedFilters={handleResetToSaved}
          saveFiltersForAll={() => handleSaveForAll(filters)}
        />
      </div>
      {isSprint && (
        <div
          className="header-bar flex justify-between items-center"
          style={{ backgroundColor: colorTokens.yellow[100], height: '52px' }}
        >
          <SprintHeader
            sprint={activeSprintQuery.data?.sprint}
            dataSource={dataSource}
            statusOptions={statusOptions}
            estimationType={workspaceQuery.data?.estimationType}
            endSprint={() => toggleEndSprintModal(true)}
          />
        </div>
      )}
      {showZeroState && (
        <TasksZeroState
          onImportClick={props.onImportClick}
          viewGroup={props.activeView.group}
          entityType={props.activeView.entityType}
        />
      )}
      {showTasks && (
        <div ref={divRef} className="px-[40px] py-4 overflow-scroll scroll-smooth" style={{ scrollBehavior: 'auto' }}>
          <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onDragCancel={onDragCancel}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.WhileDragging,
              },
            }}
          >
            <WorkSpaceContext.Provider value={workspaceContextValue}>
              <div className="m-auto flex gap-4">
                <div style={{ height: `${viewportHeight - 170 - (isSprint ? 52 : 0)}px` }} className="flex gap-4 pr-4">
                  <SortableContext items={columnsId}>
                    {columns.map((col) => (
                      <div key={col.status.id}>
                        <ColumnContainer
                          addTaskToColumn={handleCreateTaskInSprint}
                          key={col.status.id}
                          column={col}
                          tasks={tasksOrders[col.status.id] || []}
                          taskStatus={col.status}
                          isSprint={isSprint}
                          filterScrollKey={filterScrollKey}
                        />
                        <div className="mt-auto" />
                      </div>
                    ))}
                  </SortableContext>
                </div>
              </div>

              {createPortal(
                <div>
                  <DragOverlay>
                    {activeTask && (
                      <div className="drag-overlay-container overflow-hidden">
                        <TaskCard task={activeTask} dragging={true} />
                      </div>
                    )}
                  </DragOverlay>
                </div>,
                document.body
              )}
            </WorkSpaceContext.Provider>
          </DndContext>
        </div>
      )}
      {workspaceFramework === WorkspaceFramework.scrum && props.activeView.group === WorkspaceViewGroup.sprint && (
        <EndSprintModal
          open={endSprintModalVisible}
          onOk={handleEndSprint}
          onCancel={() => toggleEndSprintModal(false)}
          unfinishedTasksLength={unfinishedTasks.length}
        />
      )}
    </div>
  );

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === 'Column') {
      setActiveColumn(event.active.data.current.column);
      return;
    }

    if (event.active.data.current?.type === 'TaskTableDataType') {
      setActiveTask(event.active.data.current.task);
      setDraggingHapenned(true);
      return;
    }
  }

  /**
   * A board drag used to call the bare `updateTask` API and patch only local state, so the
   * table and task view learned the new status solely from the server's socket echo — and
   * saw nothing at all while the initiator was excluded from its own events. Write the
   * shared task cache like every other mutation site, through the keyed mutation so the
   * socket layer can tell this write's echo from a peer's change.
   *
   * Must stay a function declaration: everything below the component's `return` is only
   * reachable via hoisting. As a `const` arrow its initializer never ran, so every call
   * threw `ReferenceError: Cannot access 'persistStatusChange' before initialization`,
   * silently aborting onDragEnd before it persisted the order. (#2745)
   */
  async function persistStatusChange(draggedTask: Task, statusId: number): Promise<boolean> {
    // `onDragOver` optimistically rewrites the dragged card's statusId in local state so it
    // previews in the target column, and dnd-kit's `active.data.current.task` points at that
    // mutated copy by drop time — so `draggedTask.statusId` already equals the target here.
    // Comparing it would make this no-op guard fire on every real cross-column move and drop
    // the status write. The query cache is untouched by the preview, so read the true pre-drag
    // status from there.
    const cachedStatusId = (queryClient.getQueryData(queryKeys.tasks(Number(spaceId))) as Task[] | undefined)?.find(
      (t) => t.id === draggedTask.id
    )?.statusId;
    if ((cachedStatusId ?? draggedTask.statusId) === statusId) return true;

    const optimisticTask = { ...draggedTask, statusId };
    const rollbackCache = optimisticUpdateListItem<Task>(queryClient, queryKeys.tasks(Number(spaceId)), optimisticTask);

    try {
      await updateTaskMutation.mutateAsync({ taskId: draggedTask.id, task: optimisticTask });
    } catch (error) {
      rollbackCache();
      void messageApi.error('Failed to move task');
      return false;
    }

    if (
      workspaceQuery.data?.TaskStatus?.find((status) => status.id === statusId)?.group === TaskStatusGroup.completed
    ) {
      analytics.track(AnalyticsEvent.taskCompleted, taskCompletedProperties(optimisticTask, workspaceQuery.data));
    }
    return true;
  }

  /**
   * `onDragOver` rewrites the dragged card's statusId in `dataSource` so it previews in the
   * target column. Nothing ever undid that, so a drag that ended without a usable drop left
   * the card rendered under a status the server never received — a ghost that survived until
   * the next refetch. The query cache is untouched by the preview, so it holds the true
   * pre-drag status.
   */
  function restoreDraggedStatusFromCache(taskId: number) {
    const cachedStatusId = queryClient
      .getQueryData<Task[]>(queryKeys.tasks(Number(spaceId)))
      ?.find((t) => t.id === taskId)?.statusId;
    if (cachedStatusId === undefined) return;

    setDataSource((tasks) => tasks.map((t) => (t.id === taskId ? { ...t, statusId: cachedStatusId } : t)));
  }

  function onDragCancel() {
    const draggedTaskId = activeTask?.id;
    setActiveColumn(null);
    setActiveTask(null);
    if (draggedTaskId !== undefined) restoreDraggedStatusFromCache(draggedTaskId);
  }

  async function onDragEnd(event: DragEndEvent) {
    const draggedTaskId = activeTask?.id;
    setActiveColumn(null);
    setActiveTask(null);

    const { active, over } = event;

    // Dropped outside every droppable: same situation as a cancel.
    if (!over) {
      if (draggedTaskId !== undefined) restoreDraggedStatusFromCache(draggedTaskId);
      return;
    }

    const activeId = active.id;
    const overId = over.id as string;

    const task = over?.data.current?.task as Task;
    // Named `draggedTask`, not `activeTask`: a local `activeTask` shadows the state of the
    // same name across this whole function body, so reading the state above its declaration
    // hit the temporal dead zone and threw before the handler did anything. (#2745)
    const draggedTask = active?.data.current?.task as Task;

    const column = over?.data.current?.column as Column;

    if (task) {
      let indexWhereDropeed = -1;

      indexWhereDropeed = tasksOrders[task.statusId].findIndex((t) => t.id === +overId);
      const indexFrom = tasksOrders[task.statusId].findIndex((t) => t.id === +activeId);

      tasksOrders[task.statusId] = arrayMove(tasksOrders[task.statusId], indexFrom, indexWhereDropeed);
      settasksOrders(tasksOrders);

      // updateSpaceTasksOrder applies the new order to the workspace cache synchronously,
      // before it awaits the network, and the effect that rebuilds every column sorts by that
      // cache. persistStatusChange writes the task cache optimistically, which re-runs that
      // effect — so while the order write sat behind this await, the rebuild used the pre-drag
      // order and threw the dropped card, plus everything under it, back to where it started
      // until the write resolved. Start the order write first so the two caches never
      // disagree. (#2998)
      const orderWrite = updateSpaceTasksOrder(
        Number(spaceId),
        tasksOrders[task.statusId].map((t) => t.id),
        +activeId,
        +overId
      );
      const statusPersisted = !draggedTask || (await persistStatusChange(draggedTask, task.statusId));
      await orderWrite;
      if (!statusPersisted) {
        return;
      }
    } else if (column && draggedTask) {
      // Dropping onto an empty column: there is no `task` to read a status from, and this
      // branch used to do nothing at all — the card moved on screen and the change was
      // never sent to the server.
      // There's no specific card to drop onto here (this also covers a same-column
      // reorder that lands below the last card), so the order write was skipped
      // entirely and the reorder reverted on refresh. Move the card to the end of
      // the global order instead — since each column's displayed order is the
      // global order filtered to that column's ids, this also places it last
      // within its own column.
      const targetStatusId = column.status.id;

      // Started before the status write is awaited, for the reason in the branch above (#2998).
      const orderWrite = updateSpaceTasksOrder(Number(spaceId), [draggedTask.id]);
      const statusPersisted = await persistStatusChange(draggedTask, targetStatusId);
      await orderWrite;
      if (!statusPersisted) {
        return;
      }

      tasksOrders[targetStatusId] = [
        ...(tasksOrders[targetStatusId] || []).filter((t) => t.id !== draggedTask.id),
        draggedTask,
      ];
      settasksOrders(tasksOrders);
    }

    const isActiveAColumn = active.data.current?.type === 'Column';
    if (!isActiveAColumn) return;

    setColumns((columns) => {
      const activeColumnIndex = columns.findIndex((col) => col.id === activeId);

      const overColumnIndex = columns.findIndex((col) => col.id === overId);

      return arrayMove(columns, activeColumnIndex, overColumnIndex);
    });
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveATask = active.data.current?.type === 'TaskTableDataType';
    const isOverATask = over.data.current?.type === 'TaskTableDataType';

    if (!isActiveATask) return;

    // These rows are the same objects the React Query cache holds. Assigning to them in
    // place mutates cached state behind React Query's back and defeats memo comparisons,
    // so the moved row is replaced with a copy.
    const withStatus = (tasks: TaskTableDataType[], index: number, statusId: number) =>
      tasks.map((task, i) => (i === index ? { ...task, statusId } : task));

    // Im dropping a Task over another Task
    if (isActiveATask && isOverATask) {
      setDataSource((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const overIndex = tasks.findIndex((t) => t.id === overId);

        if (tasks[activeIndex].statusId != tasks[overIndex].statusId) {
          const moved = withStatus(tasks, activeIndex, tasks[overIndex].statusId);
          return arrayMove(moved, activeIndex, overIndex - 1);
        }

        return arrayMove(tasks, activeIndex, overIndex);
      });
    }

    const isOverAColumn = over.data.current?.type === 'Column';

    // Im dropping a Task over a column
    if (isActiveATask && isOverAColumn) {
      // A column's dnd id is its status *title*; the numeric status id lives on the payload.
      // Writing the title into statusId left the row matching no column at all.
      const overColumn = over.data.current?.column as Column | undefined;
      if (!overColumn) return;

      setDataSource((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        return arrayMove(withStatus(tasks, activeIndex, overColumn.status.id), activeIndex, activeIndex);
      });
    }
  }
};

export default Board;
